/**
 * Indicadores de cronômetro produtivo e prazo corrido da Ordem de Serviço.
 *
 * RELEASE: 2026-10-09 v1.4.1 - Aguardando revisão usa o azul informativo em vez do primary neutro.
 * RELEASE: 2026-10-09 v1.4.0 - Cronômetro usa cor semântica conforme a situação da OS.
 * RELEASE: 2026-10-09 v1.3.1 - Corrige contraste do tempo quando o cronômetro está pausado.
 * RELEASE: 2026-10-09 v1.3.0 - Prazo adota densidade, tipografia e ícone do pill oficial da Retenção.
 * RELEASE: 2026-10-09 v1.2.0 - Badge de prazo no formato pill da Retenção (fundo suave, padding equilibrado, cantos arredondados).
 * RELEASE: 2026-10-09 v1.1.0 - Chip de prazo mais rente ao texto (removido preenchimento vertical, line-height reduzido).
 * RELEASE: 2026-10-09 v1.0.0 - Componentes compartilhados para lista e Kanban.
 */
import { useEffect, useState } from 'react';
import { PauseCircle, Timer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatServiceOrderDeadline, formatServiceOrderDuration, serviceOrderDeadlineClass } from '@/lib/serviceOrderTiming';
import type { ServiceOrderStatus } from '@/hooks/useServiceOrders';

interface TimingProps {
  seconds: number;
  running?: boolean;
  paused?: boolean;
  compact?: boolean;
  status?: ServiceOrderStatus;
}

/** Mantém a mesma leitura visual da lista: execução verde, revisão azul, pausa amarela e estados parados neutros. */
export function serviceOrderTimerTone(status?: ServiceOrderStatus, paused?: boolean): string {
  if (paused) return 'text-warning';
  if (status === 'in_progress') return 'text-success';
  if (status === 'awaiting_review') return 'text-info';
  return 'text-muted-foreground';
}

/** Atualiza visualmente o valor enquanto a OS está efetivamente em execução. */
export function ServiceOrderTimer({ seconds, running, paused, compact, status }: TimingProps) {
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (!running || paused) return undefined;
    const interval = window.setInterval(() => setNow((value) => value + 1), 1000);
    return () => window.clearInterval(interval);
  }, [running, paused]);
  const Icon = paused ? PauseCircle : Timer;
  return (
    <span className={cn('inline-flex items-center gap-1 tabular-nums', compact ? 'text-[11px]' : 'text-xs', serviceOrderTimerTone(status, paused))}>
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {formatServiceOrderDuration(seconds + (running && !paused ? now : 0))}
    </span>
  );
}

/** Exibe o prazo desde a criação até a primeira revisão (ou agora). */
export function ServiceOrderDeadline({ seconds, compact }: Pick<TimingProps, 'seconds' | 'compact'>) {
  return (
    <span className={cn('inline-flex items-center justify-center gap-px rounded-full px-1 py-px font-medium tabular-nums whitespace-nowrap', compact ? 'text-[10px]' : 'text-xs', serviceOrderDeadlineClass(seconds))}>
      <Timer className="h-3 w-3 shrink-0" />
      {formatServiceOrderDeadline(seconds)}
    </span>
  );
}